import React, { useEffect, useRef, useState } from 'react';

const AdSenseAd = ({ adCode }) => {
  const adRef = useRef(null);
  const containerRef = useRef(null);
  const initialized = useRef(false);
  const [isVisible, setIsVisible] = useState(false);

  // Lazy-load: only render the ad slot when it scrolls into view (or near it)
  useEffect(() => {
    if (!containerRef.current || isVisible) return;

    // Fallback: if IntersectionObserver isn't available, just render immediately
    if (typeof IntersectionObserver === 'undefined') {
      setIsVisible(true);
      return;
    }

    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            setIsVisible(true);
            observer.disconnect();
          }
        });
      },
      {
        // Start loading the ad ~400px before it enters viewport for smoother UX
        rootMargin: '400px 0px',
        threshold: 0.01
      }
    );

    observer.observe(containerRef.current);

    return () => {
      observer.disconnect();
    };
  }, [isVisible]);

  // Inject the <ins> and call push() ONLY after the slot becomes visible
  useEffect(() => {
    if (!isVisible || !adRef.current || !adCode || initialized.current) return;

    try {
      const tempDiv = document.createElement('div');
      tempDiv.innerHTML = adCode;

      const insElement = tempDiv.querySelector('ins.adsbygoogle');
      if (!insElement) return;

      // Skip if container already has a processed ins (HMR / Strict Mode safety)
      const existingIns = adRef.current.querySelector('ins.adsbygoogle');
      if (existingIns && existingIns.getAttribute('data-adsbygoogle-status')) {
        initialized.current = true;
        return;
      }

      adRef.current.innerHTML = '';
      const clonedIns = insElement.cloneNode(true);
      clonedIns.style.display = 'block';
      clonedIns.style.width = '100%';
      adRef.current.appendChild(clonedIns);

      // Use requestIdleCallback when available so push() doesn't block the main thread
      const triggerPush = () => {
        try {
          if (clonedIns.getAttribute('data-adsbygoogle-status')) return;
          if (window.adsbygoogle) {
            (window.adsbygoogle = window.adsbygoogle || []).push({});
            initialized.current = true;
          }
        } catch (e) {
          // Silent — AdSense errors are non-critical
        }
      };

      if ('requestIdleCallback' in window) {
        window.requestIdleCallback(triggerPush, { timeout: 500 });
      } else {
        setTimeout(triggerPush, 100);
      }
    } catch (error) {
      console.error('AdSense initialization error:', error);
    }
  }, [isVisible, adCode]);

  if (!adCode) return null;

  return (
    <div
      ref={containerRef}
      className="w-full my-6"
      data-testid="adsense-ad-container"
      // Reserve space to avoid layout shift (CLS) once ad loads
      style={{ minHeight: '250px', contain: 'layout' }}
    >
      {isVisible && (
        <div
          ref={adRef}
          className="w-full mx-auto"
          style={{
            minHeight: '250px',
            maxWidth: '100%',
            display: 'block'
          }}
        />
      )}
    </div>
  );
};

export default AdSenseAd;
